import { postJwtRefreshToken } from '@framework/api-client';
import {
  AuthError,
  AuthErrorCode,
  AuthHeaderFlags,
  isResponse401,
} from '@framework/auth';
import type { IRootAuthState, IRootClientConfigState } from '@framework/auth';
import type { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore';
import { message } from 'antd';
import { AxiosError, AxiosHeaders } from 'axios';
import type { AxiosInstance } from 'axios';
import { omit } from 'lodash';
import { AuthActions } from '../state/AuthSlice';

type Subscriber = (err: null | Error) => void;

const simulatedExpiredRefreshTokenError = new AxiosError(
  "Simulated error: 'Invalid refresh token'",
  '401',
  undefined,
  undefined,
  {
    data: {},
    status: 401,
    config: {
      url: '/jwt/utente',
    } as any,
    headers: {},
    statusText: 'Unauthorized',
  },
);

export function refreshTokenInterceptor(
  axios: AxiosInstance,
  store: ToolkitStore<IRootAuthState & IRootClientConfigState>,
) {
  let isRefreshing = false;

  /**
   * Insieme delle richieste in attesa.
   */
  let subscribers: Subscriber[] = [];

  const subscribeTokenRefresh = (sub: Subscriber) => subscribers.push(sub);
  const onTokenRefreshed = (err: null | Error) => {
    subscribers.forEach(subscriber => subscriber(err));
    subscribers = [];
    isRefreshing = false;
  };

  /**
   * Funzione che tenta di effettuare il refresh del token.
   */
  function runRefreshToken() {
    /**
     * In caso di errore per utente non autenticato, significa che l'`accessToken`
     * è scaduto. In questo caso, prima di mostrare nuovamente la pagina di login
     * all'utente andiamo a tentare l'aggiornamento mediante il `refreshToken`.
     */
    if (!isRefreshing) {
      isRefreshing = true;

      const { roleId, officeId, legalEntityId } = store.getState().auth;

      /**
       * Registriamo il token come scaduto all'interno dello store.
       * Al momento non viene utilizzato, ma potrebbe essere utile.
       * Nota: non effettuiamo il logout perché altrimenti verrebbero
       * applicati i redirect di `react-router`
       */
      // store.dispatch(AuthActions.tokenExpired());

      postJwtRefreshToken(
        {
          idRuolo: roleId ?? undefined,
          idUfficio: officeId ?? undefined,
          idPersonaGiuridica: legalEntityId ?? undefined,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${store.getState().auth.refreshToken ?? ''}`,
            'x-web-refresh': true,
          },
        },
      )
        .then(res => {
          if (store.getState().auth.simulateExpiredRefreshToken) {
            store.dispatch(AuthActions.logout({ isRefreshTokenExpired: true }));
            onTokenRefreshed(simulatedExpiredRefreshTokenError);
            store.dispatch(AuthActions.simulateExpiredRefreshToken(false));
            return;
          }
          /**
           * L'aggiornamento del token ha avuto successo.
           * Registro il nonce sullo store di redux, per sapere in generale
           * che l'utente è ora autenticato.
           */
          store.dispatch(
            AuthActions.tokenRefreshed({
              accessToken: res.accessToken!,
              refreshToken: res.refreshToken!,
              legalEntityId: legalEntityId ?? null,
            }),
          );

          /**
           * Aggiorno il token per le richiesta con il token scaduto.
           */
          onTokenRefreshed(null);
        })
        .catch(err => {
          console.error('FORCED LOGOUT', err);
          /**
           * Se il refreshToken non è più valido effettuiamo il logout.
           */
          // TODO/LOW Salvare l'errore e mostrarlo nell'interfaccia, possibilmente sopra il modulo di login
          message.error(`La sessione è scaduta. Si prega di effettuare nuovamente l'accesso.`); // prettier-ignore
          store.dispatch(AuthActions.logout({ isRefreshTokenExpired: true }));

          onTokenRefreshed(err);
        });
    }
  }

  /**
   * Aggiungiamo il token alla richiesta.
   */
  axios.interceptors.request.use(config => {
    const { accessToken } = store.getState().auth;
    // console.log(`[Auth/request] add access token..`); // prettier-ignore

    if (!config.headers) config.headers = new AxiosHeaders();

    if (!config.headers?.Authorization && accessToken) {
      // console.log(`[Auth/request] OK, Adding access token to request`);
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  });

  /**
   * Se non c'è un access token, ma c'è un refresh token, allora proviamo a
   * fare il refresh _prima_ che la richiesta stessa fallisca.
   *
   * ATTENZIONE: Deve stare _dopo_ l'interceptor che aggiunge il token alla
   * richiesta, perché Axios considera gli interceptor in ordine _inverso_.
   */
  axios.interceptors.request.use(config => {
    const { accessToken, refreshToken, isAccessTokenExpired } =
      store.getState().auth;
    // console.log(`[Auth/request] ${config.url ?? ''}`); // prettier-ignore

    // Se l'access token è scaduto, e stiamo provando una richiesta in polling,
    // la ignoriamo. Verrà ritentata correttamente quando l'utente effettuerà
    // il login o rinnoverà la sessione.
    if (
      config.headers?.[AuthHeaderFlags.IsPolling] === 'true' &&
      isAccessTokenExpired
    ) {
      return Promise.reject(
        new AuthError('Non sei autenticato', AuthErrorCode.NoAccessToken),
      );
    }

    // Se stiamo provando a caricare il profilo, ma non abbiamo un access token
    // È inutile continuare.
    if (config.url?.endsWith('/jwt/utente') && !accessToken) {
      // console.log(`[Auth/request] no access token`, { refreshToken }); // prettier-ignore
      if (!refreshToken) {
        console.log(`[Auth/request] no refresh token`);
        throw new AuthError(
          'Non sei autenticato',
          AuthErrorCode.NoRefreshToken,
        );
      }

      runRefreshToken();

      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(err => {
          if (err) return reject(err);
          resolve(config);
        });
      });
    }

    return config;
  });

  axios.interceptors.response.use(undefined, error => {
    const { config, response } = error;
    const originalConfig = config;

    if (response == null || !isResponse401(response)) {
      return Promise.reject(error);
    }

    /**
     * Se riceviamo un 401 da una richiesta di refresh token, allora la sessione
     * è scaduta.
     */
    if (config.url?.includes('/jwt/refresh-token')) {
      return Promise.reject(error);
    }

    /**
     * Non ritentiamo la richiesta se si tratta di una richiesta di notifiche,
     * in quanto non dobbiamo prolungare la sessione.
     */
    if (config.headers?.[AuthHeaderFlags.IsPolling] === 'true') {
      store.dispatch(AuthActions.accessTokenExpired());
      return Promise.reject(error);
    }

    const { isLogged, isPrelogged, refreshToken } = store.getState().auth;
    console.log(`[Auth/refresh] Utente ${isLogged || isPrelogged ? '- tentativo di refresh del token (401)' : '- non autenticato'}`); // prettier-ignore

    // if (currentUser) {
    //   store.dispatch(AuthActions.logout());
    //   void message.error(`La sessione è scaduta. Si prega di effettuare nuovamente l'accesso.`); // prettier-ignore
    //   return Promise.reject(error);
    // }

    if (!isLogged && !isPrelogged) {
      return Promise.reject(error);
    }

    if (refreshToken == null) {
      store.dispatch(AuthActions.logout());
      return Promise.reject(error);
    }

    runRefreshToken();

    /**
     * Inseriamo la richiesta nella coda.
     */
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh(err => {
        if (err) return reject(err);

        return resolve(
          axios({
            ...config,
            // Rimuoviamo il vecchio access-token
            headers: omit(config.headers ?? {}, 'Authorization'),
          }),
        );
      });
    });
  });

  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.DEBUG_REFRESH_TOKEN_EXPIRATION
  ) {
    renderDebugTools(store);
    axios.interceptors.request.use(config => {
      if (isMockedAccessTokenExpired) {
        isMockedAccessTokenExpired = false;
        return Promise.reject(
          new AxiosError(
            'Force access token request',
            '401',
            config,
            undefined,
            {
              data: {},
              status: 401,
              config,
              headers: {},
              statusText: '',
            },
          ),
        );
      }

      if (
        store.getState().auth.simulateExpiredRefreshToken &&
        !config.url?.includes('/jwt/refresh-token')
      ) {
        return Promise.reject(
          new AxiosError(
            'Force refesh token request',
            '401',
            config,
            undefined,
            {
              data: {},
              status: 401,
              config,
              headers: {},
              statusText: '',
            },
          ),
        );
      }
      return config;
    });
  }
}

let isMockedAccessTokenExpired = false;

function renderDebugTools(store: ToolkitStore<IRootAuthState>) {
  // Simulate invalid refresh token
  const button = document.createElement('button');
  button.innerHTML = 'Invalidate Refresh Token';
  button.style.cssText =
    'position:fixed; top:5px; right:5px; background-color:#fc5f8a; border-radius:5px; color:white; padding:10px; cursor:pointer; border:none; z-index:1000;';
  button.onclick = () => {
    store.dispatch(AuthActions.simulateExpiredRefreshToken());
  };
  document.body.appendChild(button);

  // Simulate invalid access token
  const accessTokenButton = document.createElement('button');
  accessTokenButton.innerHTML = 'Invalidate Access Token';
  accessTokenButton.style.cssText = button.style.cssText;
  accessTokenButton.style.top = '55px';
  accessTokenButton.onclick = () => {
    isMockedAccessTokenExpired = !isMockedAccessTokenExpired;
  };
  document.body.appendChild(accessTokenButton);
}
